import React from "react";

/**
 * Synergy Soft — Spinning Networking Globe Logo
 * - True 3D spinning globe (teetotum style)
 * - Networking lines drawn ON the globe surface
 * - Pulsing nodes, blue glow, animated text
 */
export default function Logo({
  size = 65,
  textColor = "#0f172a",
  accent = "#2563eb",
  secondary = "#06b6d4",
}) {
  return (
    <div className="flex items-center gap-2 overflow-hidden" aria-label="Synergy Soft logo">
      <style>{`
        /* --- Node pulse --- */
        .node {
          animation: nodePulse 2.8s ease-in-out infinite;
        }
        @keyframes nodePulse {
          0%, 100% { opacity: 0.5; r: 2; }
          50% { opacity: 1; r: 3; }
        }

        /* --- Line flow --- */
        .data-line {
          stroke-dasharray: 100;
          stroke-dashoffset: 100;
          animation: dataFlow 3s ease-in-out infinite;
        }
        .data-line:nth-child(2) { animation-delay: 1s; }
        .data-line:nth-child(3) { animation-delay: 2s; }

        @keyframes dataFlow {
          0% { stroke-dashoffset: 100; opacity: 0; }
          20% { opacity: 1; }
          100% { stroke-dashoffset: 0; opacity: 0; }
        }

        /* --- Text animation --- */
        .text-slide {
          opacity: 0;
          transform: translateX(-30px);
          animation: slideIn 1s ease-out forwards;
        }
        @keyframes slideIn {
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      <svg
        width={size}
        height={size}
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="flowGrad" x1="0" x2="1">
            <stop offset="0%" stopColor={accent} />
            <stop offset="100%" stopColor={secondary} />
          </linearGradient>
        </defs>

        {/* Static globe with network lines (no glow, no rotation) */}
        <g transform="translate(60,60)">
          {/* Sphere outline */}
          <circle r="45" fill="none" stroke="url(#flowGrad)" strokeWidth="1" opacity="0.5" />

          {/* Subtle latitude/longitude guides */}
          <g stroke={accent} strokeOpacity="0.15" fill="none">
            <ellipse rx="42" ry="15" />
            <ellipse rx="42" ry="15" transform="rotate(45)" />
            <ellipse rx="42" ry="15" transform="rotate(-45)" />
          </g>

          {/* Branching network lines on the globe surface */}
          <g stroke="url(#flowGrad)" strokeWidth="1.6" fill="none" strokeLinecap="round">
            {/* Level 0 -> Level 1 */}
            <path className="data-line" d="M -30 -15 Q -10 -25 10 -5" />

            {/* Level 1 -> Level 2 (three branches) */}
            <path className="data-line" d="M 10 -5 Q 22 -15 30 -20" />
            <path className="data-line" d="M 10 -5 Q 18 5 25 10" />
            <path className="data-line" d="M 10 -5 Q 5 10 -5 25" />

            {/* From each Level 2 node -> two branches (Level 3) */}
            {/* From (30,-20) */}
            <path className="data-line" d="M 30 -20 Q 35 -12 40 -5" />
            <path className="data-line" d="M 30 -20 Q 25 -30 20 -35" />

            {/* From (25,10) */}
            <path className="data-line" d="M 25 10 Q 33 15 40 20" />
            <path className="data-line" d="M 25 10 Q 18 22 10 35" />

            {/* From (-5,25) */}
            <path className="data-line" d="M -5 25 Q -18 30 -25 35" />
            <path className="data-line" d="M -5 25 Q 5 32 15 40" />
          </g>

          {/* Nodes at each branching point */}
          <g fill={accent}>
            {/* Level 0 and Level 1 */}
            <circle className="node" cx="-30" cy="-15" r="2" />
            <circle className="node" cx="10" cy="-5" r="2" />

            {/* Level 2 */}
            <circle className="node" cx="30" cy="-20" r="2" />
            <circle className="node" cx="25" cy="10" r="2" />
            <circle className="node" cx="-5" cy="25" r="2" />

            {/* Level 3 */}
            <circle className="node" cx="40" cy="-5" r="2" />
            <circle className="node" cx="20" cy="-35" r="2" />
            <circle className="node" cx="40" cy="20" r="2" />
            <circle className="node" cx="10" cy="35" r="2" />
            <circle className="node" cx="-25" cy="35" r="2" />
            <circle className="node" cx="15" cy="40" r="2" />
          </g>
        </g>
      </svg>

      {/* Animated text */}
      <div className="text-slide" style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontSize: 20, fontWeight: 700, color: textColor }}>Synergy</span>
          <span
            style={{
              fontSize: 20,
              fontWeight: 600,
              background: `linear-gradient(90deg, ${accent}, ${secondary})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Soft
          </span>
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
          Go Global In Seconds
        </div>
      </div>
    </div>
  );
}



//deepseek logo
// import React, { useEffect, useRef } from "react";

// export default function Logo({
//   size = 80,
//   textColor = "#0f172a",
//   accent = "#2563eb",
//   secondary = "#06b6d4",
// }) {
//   const canvasRef = useRef(null);
//   const containerRef = useRef(null);

//   useEffect(() => {
//     const canvas = canvasRef.current;
//     const ctx = canvas.getContext('2d');
//     let animationId;
//     let particles = [];
//     let nodes = [];
//     let time = 0;

//     // Set canvas size
//     canvas.width = size;
//     canvas.height = size;

//     // Particle class
//     class Particle {
//       constructor() {
//         this.reset();
//         this.angle = Math.random() * Math.PI * 2;
//         this.distance = 20 + Math.random() * 25;
//         this.speed = 0.002 + Math.random() * 0.003;
//       }

//       reset() {
//         this.x = 0;
//         this.y = 0;
//         this.z = 0;
//         this.size = Math.random() * 2 + 1;
//         this.speed = 0.002 + Math.random() * 0.003;
//         this.phase = Math.random() * Math.PI * 2;
//       }

//       update() {
//         time += 0.016;
//         this.angle += this.speed;
        
//         // 3D position
//         this.x = Math.cos(this.angle + this.phase) * this.distance;
//         this.y = Math.sin(this.angle * 1.5 + this.phase) * (this.distance * 0.6);
//         this.z = Math.sin(this.angle * 0.7 + this.phase) * 15;
        
//         // Project to 2D
//         const scale = 150 / (150 + this.z);
//         this.projX = this.x * scale + size / 2;
//         this.projY = this.y * scale + size / 2;
//         this.projSize = this.size * scale;
//       }

//       draw() {
//         if (!this.projX || !this.projY) return;
        
//         const alpha = Math.max(0.1, (this.z + 20) / 40);
//         ctx.globalAlpha = alpha;
        
//         // Glow effect
//         const gradient = ctx.createRadialGradient(
//           this.projX, this.projY, 0,
//           this.projX, this.projY, this.projSize * 3
//         );
//         gradient.addColorStop(0, accent);
//         gradient.addColorStop(0.5, secondary);
//         gradient.addColorStop(1, 'transparent');
        
//         ctx.fillStyle = gradient;
//         ctx.beginPath();
//         ctx.arc(this.projX, this.projY, this.projSize * 3, 0, Math.PI * 2);
//         ctx.fill();
        
//         // Core particle
//         ctx.globalAlpha = 1;
//         ctx.fillStyle = '#ffffff';
//         ctx.beginPath();
//         ctx.arc(this.projX, this.projY, this.projSize, 0, Math.PI * 2);
//         ctx.fill();
//       }
//     }

//     // Node class for connection points
//     class Node {
//       constructor() {
//         this.angle = Math.random() * Math.PI * 2;
//         this.distance = 15 + Math.random() * 10;
//         this.speed = 0.001 + Math.random() * 0.002;
//         this.phase = Math.random() * Math.PI * 2;
//         this.pulse = 0;
//       }

//       update() {
//         this.angle += this.speed;
//         this.x = Math.cos(this.angle + this.phase) * this.distance;
//         this.y = Math.sin(this.angle * 1.3 + this.phase) * (this.distance * 0.8);
//         this.z = Math.cos(this.angle * 0.5 + this.phase) * 10;
        
//         const scale = 150 / (150 + this.z);
//         this.projX = this.x * scale + size / 2;
//         this.projY = this.y * scale + size / 2;
        
//         this.pulse = (Math.sin(time * 4 + this.phase) + 1) * 0.5;
//       }

//       draw() {
//         const glowSize = 3 + this.pulse * 4;
//         const gradient = ctx.createRadialGradient(
//           this.projX, this.projY, 0,
//           this.projX, this.projY, glowSize * 2
//         );
//         gradient.addColorStop(0, '#ffffff');
//         gradient.addColorStop(0.3, accent);
//         gradient.addColorStop(1, 'transparent');
        
//         ctx.fillStyle = gradient;
//         ctx.beginPath();
//         ctx.arc(this.projX, this.projY, glowSize * 2, 0, Math.PI * 2);
//         ctx.fill();
        
//         ctx.fillStyle = '#ffffff';
//         ctx.beginPath();
//         ctx.arc(this.projX, this.projY, 1.5, 0, Math.PI * 2);
//         ctx.fill();
//       }
//     }

//     // Initialize particles and nodes
//     for (let i = 0; i < 60; i++) {
//       particles.push(new Particle());
//     }
    
//     for (let i = 0; i < 12; i++) {
//       nodes.push(new Node());
//     }

//     // Draw connections between nodes
//     function drawConnections() {
//       nodes.forEach((nodeA, i) => {
//         nodes.forEach((nodeB, j) => {
//           if (i < j) {
//             const dx = nodeA.projX - nodeB.projX;
//             const dy = nodeA.projY - nodeB.projY;
//             const distance = Math.sqrt(dx * dx + dy * dy);
            
//             if (distance < 40) {
//               const alpha = (40 - distance) / 40 * 0.3;
//               ctx.strokeStyle = accent;
//               ctx.globalAlpha = alpha;
//               ctx.lineWidth = 0.5;
//               ctx.beginPath();
//               ctx.moveTo(nodeA.projX, nodeA.projY);
//               ctx.lineTo(nodeB.projX, nodeB.projY);
//               ctx.stroke();
//             }
//           }
//         });
//       });
//       ctx.globalAlpha = 1;
//     }

//     // Animation loop
//     function animate() {
//       ctx.clearRect(0, 0, size, size);
      
//       // Draw connections first
//       drawConnections();
      
//       // Update and draw particles
//       particles.forEach(particle => {
//         particle.update();
//         particle.draw();
//       });
      
//       // Update and draw nodes
//       nodes.forEach(node => {
//         node.update();
//         node.draw();
//       });
      
//       // Draw central core
//       const coreGradient = ctx.createRadialGradient(
//         size/2, size/2, 0,
//         size/2, size/2, 8
//       );
//       coreGradient.addColorStop(0, '#ffffff');
//       coreGradient.addColorStop(0.4, accent);
//       coreGradient.addColorStop(1, 'transparent');
      
//       ctx.fillStyle = coreGradient;
//       ctx.beginPath();
//       ctx.arc(size/2, size/2, 8, 0, Math.PI * 2);
//       ctx.fill();
      
//       // Pulsing ring
//       const ringPulse = (Math.sin(time * 3) + 1) * 0.5;
//       ctx.strokeStyle = secondary;
//       ctx.globalAlpha = 0.3 + ringPulse * 0.3;
//       ctx.lineWidth = 1;
//       ctx.beginPath();
//       ctx.arc(size/2, size/2, 25 + ringPulse * 5, 0, Math.PI * 2);
//       ctx.stroke();
//       ctx.globalAlpha = 1;

//       animationId = requestAnimationFrame(animate);
//     }

//     animate();

//     return () => {
//       cancelAnimationFrame(animationId);
//     };
//   }, [size, accent, secondary]);

//   return (
//     <div 
//       ref={containerRef}
//       className="flex items-center gap-4 overflow-visible"
//       aria-label="Synergy Soft logo"
//       style={{ cursor: 'pointer' }}
//     >
//       <style>{`
//         @keyframes floatText {
//           0%, 100% { transform: translateY(0px); }
//           50% { transform: translateY(-2px); }
//         }
        
//         @keyframes shimmer {
//           0% { background-position: -200% center; }
//           100% { background-position: 200% center; }
//         }
        
//         .floating-text {
//           animation: floatText 3s ease-in-out infinite;
//         }
        
//         .shimmer-text {
//           background: linear-gradient(
//             90deg,
//             ${textColor} 0%,
//             ${accent} 25%,
//             ${secondary} 50%,
//             ${accent} 75%,
//             ${textColor} 100%
//           );
//           background-size: 200% auto;
//           background-clip: text;
//           -webkit-background-clip: text;
//           -webkit-text-fill-color: transparent;
//           animation: shimmer 4s linear infinite;
//         }
        
//         .glow-box {
//           position: relative;
//           border-radius: 12px;
//           padding: 8px 16px;
//           background: rgba(255, 255, 255, 0.1);
//           backdrop-filter: blur(10px);
//           border: 1px solid rgba(255, 255, 255, 0.2);
//           box-shadow: 
//             0 0 40px rgba(37, 99, 235, 0.3),
//             inset 0 1px 0 rgba(255, 255, 255, 0.2);
//         }
        
//         .glow-box::before {
//           content: '';
//           position: absolute;
//           inset: 0;
//           border-radius: 12px;
//           padding: 1px;
//           background: linear-gradient(45deg, ${accent}, ${secondary}, ${accent});
//           mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
//           mask-composite: xor;
//           -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
//           -webkit-mask-composite: xor;
//           animation: borderRotate 3s linear infinite;
//         }
        
//         @keyframes borderRotate {
//           0% { filter: hue-rotate(0deg); }
//           100% { filter: hue-rotate(360deg); }
//         }
        
//         .subtitle {
//           background: linear-gradient(90deg, #6b7280, ${accent}, #6b7280);
//           background-size: 200% auto;
//           background-clip: text;
//           -webkit-background-clip: text;
//           -webkit-text-fill-color: transparent;
//           animation: shimmer 3s linear infinite;
//         }
//       `}</style>

//       {/* Revolutionary 3D Canvas Animation */}
//       <div style={{ position: 'relative', display: 'inline-block' }}>
//         <canvas
//           ref={canvasRef}
//           style={{
//             display: 'block',
//             borderRadius: '50%',
//             boxShadow: `
//               0 0 60px ${accent}40,
//               inset 0 0 40px ${accent}20
//             `
//           }}
//         />
        
//         {/* Central glow effect */}
//         <div style={{
//           position: 'absolute',
//           top: '50%',
//           left: '50%',
//           transform: 'translate(-50%, -50%)',
//           width: '4px',
//           height: '4px',
//           borderRadius: '50%',
//           background: '#ffffff',
//           boxShadow: `0 0 20px 8px ${accent}`,
//           pointerEvents: 'none'
//         }} />
//       </div>

//       {/* Enhanced Text with Glass Morphism */}
//       <div className="floating-text">
//         <div className="glow-box">
//           <div style={{ display: "flex", flexDirection: "column", position: 'relative', zIndex: 10 }}>
//             <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
//               <span style={{ 
//                 fontSize: 24, 
//                 fontWeight: 800, 
//                 letterSpacing: '-0.5px'
//               }} className="shimmer-text">
//                 Synergy
//               </span>
//               <span
//                 style={{
//                   fontSize: 24,
//                   fontWeight: 700,
//                   background: `linear-gradient(135deg, ${accent}, ${secondary})`,
//                   WebkitBackgroundClip: "text",
//                   WebkitTextFillColor: "transparent",
//                   filter: 'drop-shadow(0 0 8px rgba(37, 99, 235, 0.4))'
//                 }}
//               >
//                 Soft
//               </span>
//             </div>
//             <div style={{ 
//               fontSize: 11, 
//               fontWeight: 600,
//               letterSpacing: '1px',
//               marginTop: 4
//             }} className="subtitle">
//               CONNECTING WORLDS
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }




// claude logo
// import React, { useState, useEffect } from "react";

// /**
//  * Synergy Soft — ULTRA DYNAMIC LOGO
//  * - 3D rotating globe with depth perspective
//  * - Particle system orbiting the globe
//  * - Energy pulses radiating outward
//  * - Holographic shimmer effect
//  * - Interactive hover state
//  */
// export default function Logo({
//   size = 80,
//   textColor = "#0f172a",
//   accent = "#2563eb",
//   secondary = "#06b6d4",
// }) {
//   const [isHovered, setIsHovered] = useState(false);
//   const [particles, setParticles] = useState([]);

//   // Generate orbital particles
//   useEffect(() => {
//     const newParticles = Array.from({ length: 20 }, (_, i) => ({
//       id: i,
//       angle: (i * 360) / 20,
//       radius: 50 + Math.random() * 10,
//       speed: 0.5 + Math.random() * 0.5,
//       size: 1 + Math.random() * 2,
//     }));
//     setParticles(newParticles);
//   }, []);

//   return (
//     <div className="flex items-center gap-3 overflow-visible" aria-label="Synergy Soft logo">
//       <style>{`
//         /* --- 3D Globe Rotation --- */
//         @keyframes rotate3D {
//           from { transform: perspective(500px) rotateY(0deg) rotateX(5deg); }
//           to { transform: perspective(500px) rotateY(360deg) rotateX(5deg); }
//         }

//         .globe-3d {
//           animation: rotate3D 8s linear infinite;
//           transform-style: preserve-3d;
//         }

//         .globe-3d.hovered {
//           animation-play-state: paused;
//           transform: perspective(500px) rotateY(45deg) rotateX(10deg) scale(1.1);
//           transition: transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
//         }

//         /* --- Energy Pulse Rings --- */
//         @keyframes energyPulse {
//           0% {
//             r: 45;
//             opacity: 0.8;
//             stroke-width: 2;
//           }
//           100% {
//             r: 70;
//             opacity: 0;
//             stroke-width: 0.5;
//           }
//         }

//         .energy-ring {
//           animation: energyPulse 2s ease-out infinite;
//         }

//         .energy-ring:nth-child(2) {
//           animation-delay: 0.7s;
//         }

//         .energy-ring:nth-child(3) {
//           animation-delay: 1.4s;
//         }

//         /* --- Particle Orbit --- */
//         @keyframes orbitParticle {
//           from { transform: rotate(0deg) translateX(55px) rotate(0deg); }
//           to { transform: rotate(360deg) translateX(55px) rotate(-360deg); }
//         }

//         .particle {
//           animation: orbitParticle var(--duration) linear infinite;
//           filter: blur(0.5px);
//         }

//         /* --- Node Pulse with Glow --- */
//         @keyframes nodePulseGlow {
//           0%, 100% {
//             opacity: 0.6;
//             filter: drop-shadow(0 0 2px currentColor);
//           }
//           50% {
//             opacity: 1;
//             filter: drop-shadow(0 0 6px currentColor) drop-shadow(0 0 10px currentColor);
//           }
//         }

//         .node {
//           animation: nodePulseGlow 2s ease-in-out infinite;
//         }

//         /* --- Data Flow Enhanced --- */
//         @keyframes dataFlowEnhanced {
//           0% {
//             stroke-dashoffset: 100;
//             opacity: 0;
//             filter: drop-shadow(0 0 0px currentColor);
//           }
//           30% {
//             opacity: 1;
//             filter: drop-shadow(0 0 4px currentColor);
//           }
//           100% {
//             stroke-dashoffset: 0;
//             opacity: 0;
//             filter: drop-shadow(0 0 0px currentColor);
//           }
//         }

//         .data-line {
//           stroke-dasharray: 100;
//           animation: dataFlowEnhanced 2.5s ease-in-out infinite;
//         }

//         .data-line:nth-child(odd) { animation-delay: 0.3s; }
//         .data-line:nth-child(even) { animation-delay: 0.6s; }

//         /* --- Holographic Shimmer --- */
//         @keyframes shimmer {
//           0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
//           100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
//         }

//         .holographic-shine {
//           animation: shimmer 3s ease-in-out infinite;
//         }

//         /* --- Text Reveal with Glitch --- */
//         @keyframes textReveal {
//           0% {
//             opacity: 0;
//             transform: translateY(10px);
//             filter: blur(4px);
//           }
//           100% {
//             opacity: 1;
//             transform: translateY(0);
//             filter: blur(0);
//           }
//         }

//         .text-reveal {
//           animation: textReveal 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
//         }

//         .text-reveal:nth-child(1) { animation-delay: 0.3s; }
//         .text-reveal:nth-child(2) { animation-delay: 0.5s; }

//         /* --- Gradient Shift --- */
//         @keyframes gradientShift {
//           0%, 100% { stop-color: ${accent}; }
//           50% { stop-color: ${secondary}; }
//         }

//         .gradient-animate stop:first-child {
//           animation: gradientShift 4s ease-in-out infinite;
//         }

//         /* --- Core Glow --- */
//         @keyframes coreGlow {
//           0%, 100% {
//             opacity: 0.3;
//             r: 8;
//           }
//           50% {
//             opacity: 0.8;
//             r: 12;
//           }
//         }

//         .core-glow {
//           animation: coreGlow 2s ease-in-out infinite;
//         }
//       `}</style>

//       <div
//         className="relative"
//         onMouseEnter={() => setIsHovered(true)}
//         onMouseLeave={() => setIsHovered(false)}
//         style={{ width: size, height: size }}
//       >
//         <svg
//           width={size}
//           height={size}
//           viewBox="0 0 140 140"
//           xmlns="http://www.w3.org/2000/svg"
//           style={{ overflow: "visible" }}
//         >
//           <defs>
//             <linearGradient id="flowGrad" x1="0" y1="0" x2="1" y2="1" className="gradient-animate">
//               <stop offset="0%" stopColor={accent} />
//               <stop offset="100%" stopColor={secondary} />
//             </linearGradient>

//             <radialGradient id="glowGrad">
//               <stop offset="0%" stopColor={accent} stopOpacity="0.8" />
//               <stop offset="100%" stopColor={secondary} stopOpacity="0" />
//             </radialGradient>

//             <filter id="glow">
//               <feGaussianBlur stdDeviation="3" result="coloredBlur" />
//               <feMerge>
//                 <feMergeNode in="coloredBlur" />
//                 <feMergeNode in="SourceGraphic" />
//               </feMerge>
//             </filter>

//             <linearGradient id="shineGrad" x1="0" y1="0" x2="1" y2="1">
//               <stop offset="0%" stopColor="white" stopOpacity="0" />
//               <stop offset="50%" stopColor="white" stopOpacity="0.6" />
//               <stop offset="100%" stopColor="white" stopOpacity="0" />
//             </linearGradient>
//           </defs>

//           <g transform="translate(70,70)">
//             {/* Energy pulse rings */}
//             <circle className="energy-ring" r="45" fill="none" stroke="url(#flowGrad)" strokeWidth="2" opacity="0.8" />
//             <circle className="energy-ring" r="45" fill="none" stroke="url(#flowGrad)" strokeWidth="2" opacity="0.8" />
//             <circle className="energy-ring" r="45" fill="none" stroke="url(#flowGrad)" strokeWidth="2" opacity="0.8" />

//             {/* Orbital particles */}
//             {particles.map((p) => (
//               <circle
//                 key={p.id}
//                 className="particle"
//                 r={p.size}
//                 fill={p.id % 2 === 0 ? accent : secondary}
//                 opacity="0.7"
//                 style={{
//                   "--duration": `${6 + p.speed * 4}s`,
//                   animationDelay: `${-p.angle / 360 * (6 + p.speed * 4)}s`,
//                 }}
//               />
//             ))}

//             {/* 3D Globe */}
//             <g className={`globe-3d ${isHovered ? "hovered" : ""}`}>
//               {/* Core glow */}
//               <circle className="core-glow" r="10" fill="url(#glowGrad)" opacity="0.5" />

//               {/* Main sphere */}
//               <circle r="45" fill="rgba(15, 23, 42, 0.05)" stroke="url(#flowGrad)" strokeWidth="1.5" filter="url(#glow)" />

//               {/* Latitude/longitude grid */}
//               <g stroke={accent} strokeOpacity="0.2" fill="none" strokeWidth="0.8">
//                 <ellipse rx="45" ry="12" />
//                 <ellipse rx="45" ry="12" transform="rotate(60)" />
//                 <ellipse rx="45" ry="12" transform="rotate(-60)" />
//                 <ellipse rx="35" ry="45" />
//                 <ellipse rx="20" ry="45" />
//               </g>

//               {/* Network lines with glow */}
//               <g stroke="url(#flowGrad)" strokeWidth="2" fill="none" strokeLinecap="round">
//                 <path className="data-line" d="M -30 -15 Q -10 -25 10 -5" />
//                 <path className="data-line" d="M 10 -5 Q 22 -15 30 -20" />
//                 <path className="data-line" d="M 10 -5 Q 18 5 25 10" />
//                 <path className="data-line" d="M 10 -5 Q 5 10 -5 25" />
//                 <path className="data-line" d="M 30 -20 Q 35 -12 40 -5" />
//                 <path className="data-line" d="M 30 -20 Q 25 -30 20 -35" />
//                 <path className="data-line" d="M 25 10 Q 33 15 40 20" />
//                 <path className="data-line" d="M 25 10 Q 18 22 10 35" />
//                 <path className="data-line" d="M -5 25 Q -18 30 -25 35" />
//                 <path className="data-line" d="M -5 25 Q 5 32 15 40" />
//               </g>

//               {/* Glowing nodes */}
//               <g fill={accent}>
//                 <circle className="node" cx="-30" cy="-15" r="2.5" />
//                 <circle className="node" cx="10" cy="-5" r="2.5" />
//                 <circle className="node" cx="30" cy="-20" r="2.5" />
//                 <circle className="node" cx="25" cy="10" r="2.5" />
//                 <circle className="node" cx="-5" cy="25" r="2.5" />
//                 <circle className="node" cx="40" cy="-5" r="2.5" />
//                 <circle className="node" cx="20" cy="-35" r="2.5" />
//                 <circle className="node" cx="40" cy="20" r="2.5" />
//                 <circle className="node" cx="10" cy="35" r="2.5" />
//                 <circle className="node" cx="-25" cy="35" r="2.5" />
//                 <circle className="node" cx="15" cy="40" r="2.5" />
//               </g>

//               {/* Holographic shine overlay */}
//               <g clipPath="circle(45px)" opacity="0.3">
//                 <rect
//                   className="holographic-shine"
//                   x="-45"
//                   y="-45"
//                   width="30"
//                   height="150"
//                   fill="url(#shineGrad)"
//                 />
//               </g>
//             </g>
//           </g>
//         </svg>
//       </div>

//       {/* Animated text with reveals */}
//       <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
//         <div className="text-reveal" style={{ display: "flex", alignItems: "baseline", gap: 6, opacity: 0 }}>
//           <span style={{ fontSize: 24, fontWeight: 800, color: textColor, letterSpacing: "-0.02em" }}>
//             Synergy
//           </span>
//           <span
//             style={{
//               fontSize: 24,
//               fontWeight: 700,
//               background: `linear-gradient(135deg, ${accent}, ${secondary}, ${accent})`,
//               backgroundSize: "200% 100%",
//               WebkitBackgroundClip: "text",
//               WebkitTextFillColor: "transparent",
//               animation: "gradientShift 3s ease infinite",
//             }}
//           >
//             Soft
//           </span>
//         </div>
//         <div
//           className="text-reveal"
//           style={{
//             fontSize: 11,
//             color: "#6b7280",
//             textTransform: "uppercase",
//             letterSpacing: "0.1em",
//             fontWeight: 600,
//             opacity: 0,
//           }}
//         >
//           Go Global In Seconds
//         </div>
//       </div>
//     </div>
//   );
// }




// // grok logo 
// import React, { useEffect, useRef } from 'react';
// import * as THREE from 'three';

// const SynergySoftLogo = ({ size = 100 }) => {
//   const canvasRef = useRef(null);
//   const sceneRef = useRef(null);
//   const cameraRef = useRef(null);
//   const rendererRef = useRef(null);
//   const animationRef = useRef(null);

//   useEffect(() => {
//     // Initialize Three.js scene
//     const scene = new THREE.Scene();
//     const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
//     const renderer = new THREE.WebGLRenderer({ 
//       canvas: canvasRef.current, 
//       alpha: true,
//       antialias: true 
//     });
    
//     renderer.setSize(size, size);
//     camera.position.z = 100;

//     sceneRef.current = scene;
//     cameraRef.current = camera;
//     rendererRef.current = renderer;

//     // LARGER GLOBE SIZE - Increased from 25 to 40
//     const globeGeometry = new THREE.SphereGeometry(40, 32, 32);
//     const globeMaterial = new THREE.MeshBasicMaterial({
//       color: 0x2563eb,
//       wireframe: true,
//       transparent: true,
//       opacity: 0.2,
//     });
//     const globe = new THREE.Mesh(globeGeometry, globeMaterial);
//     scene.add(globe);

//     // SCALED UP NODE POSITIONS (proportional to larger globe)
//     const nodes = [];
//     const nodePositions = [
//       [-32, -16, 16], [16, -8, 24], [32, -24, 8], [24, 16, 16], [-8, 32, 8],
//       [40, -8, 0], [24, -40, 8], [40, 24, 0], [8, 48, 8], [-24, 40, 0], [16, 56, 8]
//     ];
    
//     nodePositions.forEach(pos => {
//       const nodeGeo = new THREE.SphereGeometry(1.2, 16, 16); // Slightly larger nodes
//       const nodeMat = new THREE.MeshBasicMaterial({ color: 0x06b6d4 });
//       const node = new THREE.Mesh(nodeGeo, nodeMat);
//       node.position.set(...pos);
//       scene.add(node);
//       nodes.push(node);
//     });

//     // Network lines
//     const lines = [];
//     const linePaths = [
//       [[-32, -16, 16], [16, -8, 24]],
//       [[16, -8, 24], [32, -24, 8]],
//       [[16, -8, 24], [24, 16, 16]],
//       [[16, -8, 24], [-8, 32, 8]],
//       [[32, -24, 8], [40, -8, 0]],
//       [[32, -24, 8], [24, -40, 8]],
//       [[24, 16, 16], [40, 24, 0]],
//       [[24, 16, 16], [8, 48, 8]],
//       [[-8, 32, 8], [-24, 40, 0]],
//       [[-8, 32, 8], [16, 56, 8]],
//     ];
    
//     linePaths.forEach(path => {
//       const material = new THREE.LineBasicMaterial({ 
//         color: 0x2563eb, 
//         transparent: true, 
//         opacity: 0.5 
//       });
//       const points = path.map(p => new THREE.Vector3(...p));
//       const geometry = new THREE.BufferGeometry().setFromPoints(points);
//       const line = new THREE.Line(geometry, material);
//       scene.add(line);
//       lines.push(line);
//     });

//     // SCALED UP Particle system
//     const particleCount = 60; // More particles for larger globe
//     const particles = new THREE.BufferGeometry();
//     const positions = new Float32Array(particleCount * 3);
//     const velocities = [];
    
//     for (let i = 0; i < particleCount; i++) {
//       const theta = Math.random() * Math.PI * 2;
//       const phi = Math.random() * Math.PI;
//       // Increased radius for larger globe
//       const r = 45 + Math.random() * 8;
//       positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
//       positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
//       positions[i * 3 + 2] = r * Math.cos(phi);
//       velocities.push(new THREE.Vector3(
//         (Math.random() - 0.5) * 0.1,
//         (Math.random() - 0.5) * 0.1,
//         (Math.random() - 0.5) * 0.1
//       ));
//     }
    
//     particles.setAttribute('position', new THREE.BufferAttribute(positions, 3));
//     const particleMaterial = new THREE.PointsMaterial({
//       color: 0x06b6d4,
//       size: 1.8, // Slightly larger particles
//       transparent: true,
//       opacity: 0.7,
//     });
//     const particleSystem = new THREE.Points(particles, particleMaterial);
//     scene.add(particleSystem);

//     // Store references for animation
//     const animationObjects = {
//       globe,
//       nodes,
//       lines,
//       particleSystem,
//       velocities,
//       particleCount
//     };

//     // Animation loop
//     const animate = () => {
//       animationRef.current = requestAnimationFrame(animate);
      
//       const { globe, nodes, lines, particleSystem, velocities, particleCount } = animationObjects;
      
//       globe.rotation.y += 0.01;
      
//       nodes.forEach(node => {
//         node.scale.setScalar(1 + 0.3 * Math.sin(Date.now() * 0.002));
//       });
      
//       lines.forEach(line => {
//         line.material.opacity = 0.5 + 0.3 * Math.sin(Date.now() * 0.001);
//       });

//       const positions = particleSystem.geometry.attributes.position.array;
//       for (let i = 0; i < particleCount; i++) {
//         positions[i * 3] += velocities[i].x;
//         positions[i * 3 + 1] += velocities[i].y;
//         positions[i * 3 + 2] += velocities[i].z;
//         const dist = Math.sqrt(
//           positions[i * 3] ** 2 + 
//           positions[i * 3 + 1] ** 2 + 
//           positions[i * 3 + 2] ** 2
//         );
//         // Increased boundary for larger globe
//         if (dist > 55) {
//           const theta = Math.random() * Math.PI * 2;
//           const phi = Math.random() * Math.PI;
//           const r = 45;
//           positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
//           positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
//           positions[i * 3 + 2] = r * Math.cos(phi);
//         }
//       }
//       particleSystem.geometry.attributes.position.needsUpdate = true;
      
//       renderer.render(scene, camera);
//     };

//     animate();

//     // Cleanup
//     return () => {
//       if (animationRef.current) {
//         cancelAnimationFrame(animationRef.current);
//       }
//       if (rendererRef.current) {
//         rendererRef.current.dispose();
//       }
//     };
//   }, [size]);

//   return (
//     <div className="flex items-center gap-3 p-2 bg-white rounded-lg">
//       <canvas 
//         ref={canvasRef}
//         className="flex-shrink-0"
//         style={{ width: size, height: size }}
//       />
      
//       <div className="flex flex-col">
//         <div className="flex items-baseline gap-1">
//           <span className="glow-text text-xl font-bold text-gray-900">
//             Synergy
//           </span>
//           <span 
//             className="glow-text text-xl font-bold"
//             style={{
//               background: 'linear-gradient(90deg, #2563eb, #06b6d4)',
//               WebkitBackgroundClip: 'text',
//               WebkitTextFillColor: 'transparent',
//             }}
//           >
//             Soft
//           </span>
//         </div>
//         <div className="sub-text mt-1 text-xs text-gray-600">
//           Go Global In Seconds
//         </div>
//       </div>

//       <style jsx>{`
//         .glow-text { 
//           text-shadow: 0 0 8px rgba(37, 99, 235, 0.3), 0 0 15px rgba(6, 182, 212, 0.2); 
//           animation: neonGlow 2s ease-in-out infinite; 
//           transition: transform 0.3s ease; 
//         }
//         .glow-text:hover { 
//           transform: scale(1.05); 
//         }
//         .sub-text { 
//           text-shadow: 0 0 3px rgba(37, 99, 235, 0.2); 
//           animation: fadeIn 1.5s ease-out forwards; 
//         }
//         @keyframes neonGlow {
//           0%, 100% { 
//             text-shadow: 0 0 8px rgba(37, 99, 235, 0.3), 0 0 15px rgba(6, 182, 212, 0.2); 
//           }
//           50% { 
//             text-shadow: 0 0 12px rgba(37, 99, 235, 0.4), 0 0 20px rgba(6, 182, 212, 0.3); 
//           }
//         }
//         @keyframes fadeIn {
//           from { 
//             opacity: 0; 
//             transform: translateY(5px); 
//           }
//           to { 
//             opacity: 1; 
//             transform: translateY(0); 
//           }
//         }
//       `}</style>
//     </div>
//   );
// };

// export default SynergySoftLogo;